import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { closeOverlappingModal, hideJobCreatePaymentError } from '~/actions';
import { usePostShifts } from '~/domain/MultiDayShifts';
import { isDailyOvertime } from '~/components/Request/multiday/utils';
import { useAppSelector, useToggle } from '~/hooks';
import { fireRequestSubmitted } from '~/posthogs/events';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';

interface IUseReviewSubmissionParams {
  officeId: string;
  allowNavigation: () => void;
  getValues: () => IShiftDetailsFormValues;
  arrivalTime: string;
  departureTime: string;
  mealBreakDuration: string;
  isMealBreakPaid: boolean;
}

export const useReviewSubmission = ({
  officeId,
  allowNavigation,
  getValues,
  arrivalTime,
  departureTime,
  mealBreakDuration,
  isMealBreakPaid,
}: IUseReviewSubmissionParams) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const isOverlappingJob = useAppSelector((state) => state.job.isOverlappingJob);
  const showJobCreatePaymentError = useAppSelector((state) => state.job.showJobCreatePaymentError);
  const workerClassificationType = selectedChildOffice?.workerClassification?.classificationType;
  const overtimeLimits = workerClassificationType
    ? selectedChildOffice?.overtimeLimits?.[workerClassificationType]
    : undefined;

  const [showOvertimeLimitPopup, overtimeLimitPopupControls] = useToggle();

  const { mutate: postShifts, isPending: isPostingShifts } = usePostShifts({
    officeId,
    allowNavigation,
  });

  const handleConfirmSubmission = useCallback(
    (duplicate?: boolean) => {
      const formValues = getValues();
      fireRequestSubmitted({
        shiftsPosted: formValues.dates.length,
        totalInvitesSent: formValues.invite?.length ?? 0,
      });
      postShifts({ formValues, duplicate });
    },
    [getValues, postShifts],
  );

  const checkOvertimeAndSubmit = useCallback(() => {
    const job = {
      localStart: arrivalTime,
      localEnd: departureTime,
      break_time: mealBreakDuration,
      is_break_paid: isMealBreakPaid,
    };
    if (overtimeLimits?.daily && isDailyOvertime(job, overtimeLimits.daily)) {
      overtimeLimitPopupControls.setOn();
      return;
    }
    handleConfirmSubmission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    arrivalTime,
    departureTime,
    mealBreakDuration,
    isMealBreakPaid,
    overtimeLimits,
    handleConfirmSubmission,
  ]);

  const handleSendRequest = useCallback(() => {
    checkOvertimeAndSubmit();
  }, [checkOvertimeAndSubmit]);

  const confirmOverlapping = useCallback(() => {
    dispatch(closeOverlappingModal());
    handleConfirmSubmission(true);
  }, [dispatch, handleConfirmSubmission]);

  const goBackOverlapping = useCallback(() => {
    dispatch(closeOverlappingModal());
  }, [dispatch]);

  const hidePaymentError = useCallback(() => {
    dispatch(hideJobCreatePaymentError());
  }, [dispatch]);

  const updatePayment = useCallback(() => {
    dispatch(hideJobCreatePaymentError());
    allowNavigation();
    navigate('/account/payment');
  }, [dispatch, allowNavigation, navigate]);

  return {
    isPostingShifts,
    isOverlappingJob: !!isOverlappingJob,
    showJobCreatePaymentError,
    showOvertimeLimitPopup,
    overtimeLimitPopupControls,
    handleSendRequest,
    handleConfirmSubmission,
    confirmOverlapping,
    goBackOverlapping,
    hidePaymentError,
    updatePayment,
  };
};
